import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/utils/utils';

const appErrorFallbackVariants = cva('flex flex-col items-center justify-center gap-4 p-6 transition-colors', {
  variants: {
    variant: {
      /** Error de raíz: sustituye la aplicación completa. */
      fullScreen: 'min-h-screen bg-background text-foreground',
      /** Error de ruta: ocupa sólo el área de contenido, la navegación sigue montada. */
      inline: 'min-h-[24rem] w-full text-foreground',
    },
  },
  defaultVariants: {
    variant: 'inline',
  },
});

export interface AppErrorFallbackProps extends VariantProps<typeof appErrorFallbackVariants> {
  className?: string;
  /** Reintenta sin recargar. Si no se pasa, sólo se ofrece recargar la página. */
  onRetry?: () => void;
  'data-testid'?: string;
}

export const AppErrorFallback = ({
  variant,
  className,
  onRetry,
  'data-testid': dataTestId = 'app-error-fallback',
}: AppErrorFallbackProps) => {
  const { t } = useTranslation('ui');

  return (
    <div className={cn(appErrorFallbackVariants({ variant }), className)} role="alert" data-testid={dataTestId}>
      <p className="text-center text-title max-w-md">{t('errorFallback.message')}</p>
      <div className="flex flex-wrap gap-3 justify-center">
        {onRetry ? (
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-btn-text text-sm font-medium transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            onClick={onRetry}
          >
            {t('errorFallback.retry')}
          </button>
        ) : null}
        <button
          type="button"
          className="rounded-md border border-lines bg-card-bg px-4 py-2 text-sm font-medium text-title transition-colors hover:bg-hover-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() => window.location.reload()}
        >
          {t('errorFallback.reload')}
        </button>
      </div>
    </div>
  );
};
